import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

// The branch shown beside the page title on Home ("Home  [C30 v]"). It looks like any other drop-down box (the standard input look). A person who can switch branch
// presses it to open the branch list straight underneath; choosing a branch switches to it and closes the list. For everyone else the same box is shown but it cannot be pressed.
//   branches: [{ id, code, name? }]   currentId: the branch in use   label: its code   canSwitch: false for a box that cannot be pressed   onPick(id): switch to a branch
const V3BranchMenu = ({ branches, currentId, label, canSwitch, onPick }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => { if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!canSwitch) {
    return (
      <button type="button" role="combobox" aria-expanded="false" aria-disabled="true" disabled className="v3-branch-chip v3-branch-chip--static" data-testid="v3-branch-chip">
        <span>{label}</span>
        <ChevronDown className="v3-i v3-i--sm v3-branch-chevron" />
      </button>
    );
  }

  return (
    <div className="v3-branch" ref={rootRef}>
      <button
        type="button"
        role="combobox"
        className={`v3-branch-chip ${open ? 'v3-branch-chip--open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
        data-testid="v3-branch-button"
      >
        <span>{label}</span>
        <ChevronDown className="v3-i v3-i--sm v3-branch-chevron" />
      </button>
      {open && (
        <div className="v3-branch-menu" role="listbox" aria-label="Branch" data-testid="v3-branch-menu">
          <div className="v3-branch-menu-title">Branch</div>
          <div className="v3-branch-list" data-testid="v3-branch-list">
            {branches.map((branch) => {
              const selected = branch.id === currentId;
              return (
                <button
                  key={branch.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className="v3-branch-option"
                  onClick={() => { setOpen(false); if (!selected) onPick(branch.id); }}
                  data-testid={`v3-branch-option-${branch.id}`}
                >
                  <span className="v3-branch-code">{branch.code || branch.id}</span>
                  <span className="v3-branch-name">{branch.name || ''}</span>
                  {selected && <Check className="v3-i v3-i--sm v3-branch-check" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default V3BranchMenu;
